/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { BranchingOperation, type IBranchingSurveyItems, type Question } from '../../../features/SurveyTempleteFeature/types'
import { createSurveyTemplate, editSurveyTemplate, getSurveyByIdApi } from './apis'
import { notification } from '../../../components/Notifications'
import { v4 as uuidv4 } from 'uuid'
import { buildSurveyTemplateChangeSummary, extractSurveyErrorMessage, getBranchGroupKey, preparedBranchFilterForUpdateFilters, updateBranchingFiltersSectionNameInParentSection } from '../../../features/SurveyTempleteFeature/helpers'
import { SurveyItemQuestionType } from '../../../models/SurveyTemplate'
import {
  DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES,
  PREFIX_FOLLOW_UP_FREE_RESPONSE_QUESTION_PROMPT,
  sortTeachingRolesByPrecedence,
  type TeachingRole
} from '../../../features/SurveyTempleteFeature/constant'

export interface IEditableQuestion {
  questionIndex: number
  sectionIndex: number
  question: Question
}

export interface IAttribute {
  key: string
  op: string
  type: string
  values: string[]
  id?: string
}

export interface ISectionSettings {
  isRepeatPerInstructor?: boolean
  attributes?: IAttribute[]
  isSelectInstructorEnabled?: boolean
  selectedRoles?: TeachingRole[]
}

export interface ISurveySection {
  name: string
  title?: string | null
  descr?: string | null
  surveySectionId: string
  // @TODO need to remove the below 3 fields and move them to settings once we confirm the requirements
  isRepeatPerInstructor?: boolean
  isSelectInstructorEnabled?: boolean
  attributes?: IAttribute[]
  surveyItems?: Question[]
  indentation: number
  branchingFilters: Record<string, any> | null
  parentSurveySectionId: string | null
  revision?: number
  settings?: ISectionSettings[]
}

export interface ISelectedQuestion {
  sectionIndex: number
  questionType: string | null
  question?: Question
}

const initialSection: ISurveySection = {
  name: 'Untitled section 1',
  indentation: 0,
  surveySectionId: uuidv4(),
  parentSurveySectionId: null,
  branchingFilters: null,
  settings: [
    {
      isRepeatPerInstructor: false,
      isSelectInstructorEnabled: false
    }
  ]
}

interface ISurveyTemplate {
  selectedQuestion: ISelectedQuestion | null
  selectedSection: ISurveySection | null
  title: string
  surveySections: ISurveySection[]
  loading: boolean
  actionLoading: boolean
  editableQuestion: IEditableQuestion | null | undefined
  editableSurvey: ISurveySection | null
  branchModal: boolean
}

const initialState: ISurveyTemplate = {
  selectedQuestion: null,
  selectedSection: null,
  title: '',
  surveySections: [initialSection],
  loading: false,
  actionLoading: false,
  editableQuestion: null,
  editableSurvey: null,
  branchModal: false
}
export const createInitialSectionWithSetId = (sectionId?: string): ISurveySection => ({
  ...initialSection,
  surveySectionId: sectionId ?? uuidv4()
})

// Sorts selected teaching roles and falls back to the default branch-role set when empty.
const getNormalizedSelectedRoles = (selectedRoles?: TeachingRole[] | null): TeachingRole[] => {
  const normalizedRoles = sortTeachingRolesByPrecedence(selectedRoles ?? [])
  return normalizedRoles.length > 0 ? normalizedRoles : DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES
}

// Returns the first settings group for a section, or a disabled default when none exists.
const getSectionDefaultSetting = (section?: ISurveySection | null): ISectionSettings => {
  const sectionSettings = section?.settings
  const sectionDefaultSetting = Array.isArray(sectionSettings) ? sectionSettings[0] : undefined

  if (sectionDefaultSetting) {
    return sectionDefaultSetting
  }

  return {
    isRepeatPerInstructor: false,
    isSelectInstructorEnabled: false
  }
}

// Seeds a newly created branched section from its parent's repeat-per-instructor settings.
const getBranchedSectionInitialSettings = (parentSection?: ISurveySection): ISectionSettings[] => {
  const parentDefaultSetting = getSectionDefaultSetting(parentSection)

  if (!parentDefaultSetting?.isRepeatPerInstructor) {
    return [{ isRepeatPerInstructor: false }]
  }

  return [{
    isRepeatPerInstructor: true,
    selectedRoles: getNormalizedSelectedRoles(parentDefaultSetting.selectedRoles)
  }]
}

// Copies parent repeat-per-instructor settings onto each direct branched child when the parent is enabled.
const syncDirectBranchedChildrenWithParentSettings = (
  sections: ISurveySection[],
  parentSurveySectionId: string,
  parentSettings?: ISectionSettings[]
): void => {
  const parentDefaultSetting = Array.isArray(parentSettings) ? parentSettings[0] : undefined

  if (!parentDefaultSetting?.isRepeatPerInstructor) {
    return
  }

  const parentSelectedRoles = getNormalizedSelectedRoles(parentDefaultSetting.selectedRoles)

  sections.forEach((section) => {
    if (section.parentSurveySectionId !== parentSurveySectionId) {
      return
    }

    section.settings = [{
      isRepeatPerInstructor: true,
      selectedRoles: parentSelectedRoles
    }]
  })
}

// Collects a section and all of its descendant branches based on parent-child section links.
const getSectionSubtreeIndices = (sections: ISurveySection[], rootIndex: number): number[] => {
  if (rootIndex < 0 || rootIndex >= sections.length) return []

  const indices: number[] = [rootIndex]
  const subtreeSectionIds = new Set<string>([sections[rootIndex].surveySectionId])

  for (let index = rootIndex + 1; index < sections.length; index++) {
    const parentSectionId = sections[index].parentSurveySectionId
    if (parentSectionId && subtreeSectionIds.has(parentSectionId)) {
      indices.push(index)
      subtreeSectionIds.add(sections[index].surveySectionId)
    }
  }

  return indices
}

// Returns the final list index occupied by a section subtree.
const getSectionSubtreeLastIndex = (sections: ISurveySection[], rootIndex: number): number => {
  const subtreeIndices = getSectionSubtreeIndices(sections, rootIndex)
  return subtreeIndices[subtreeIndices.length - 1] ?? rootIndex
}

// Finds where a branched section should be inserted so matching branch groups stay contiguous.
const findBranchInsertIndex = (sections: ISurveySection[], branchSection: ISurveySection): number => {
  const parentSurveySectionId = branchSection.parentSurveySectionId
  if (!parentSurveySectionId) {
    return sections.length
  }

  const parentSectionIndex = sections.findIndex((section) => section.surveySectionId === parentSurveySectionId)
  if (parentSectionIndex === -1) {
    return sections.length
  }

  const targetGroupKey = getBranchGroupKey(branchSection)
  const matchingSiblingIndices = sections.reduce<number[]>((indices, section, index) => {
    if (
      section.parentSurveySectionId === parentSurveySectionId &&
      getBranchGroupKey(section) === targetGroupKey
    ) {
      indices.push(index)
    }

    return indices
  }, [])

  if (matchingSiblingIndices.length > 0) {
    const lastMatchingSiblingIndex = matchingSiblingIndices[matchingSiblingIndices.length - 1]
    return getSectionSubtreeLastIndex(sections, lastMatchingSiblingIndex) + 1
  }

  const siblingIndices = sections.reduce<number[]>((indices, section, index) => {
    if (section.parentSurveySectionId === parentSurveySectionId) {
      indices.push(index)
    }

    return indices
  }, [])

  if (siblingIndices.length > 0) {
    const lastSiblingIndex = siblingIndices[siblingIndices.length - 1]
    return getSectionSubtreeLastIndex(sections, lastSiblingIndex) + 1
  }

  return parentSectionIndex + 1
}

// Formats selected branch answers into the human-readable text used in generated section names.
const formatBranchSectionAnswers = (answers?: string[] | null): string => {
  const safeAnswers = answers ?? []

  if (!safeAnswers.length) {
    return ''
  }

  const quotedAnswers = safeAnswers.map((answer) => `"${answer}"`)
  if (quotedAnswers.length === 1) {
    return quotedAnswers[0]
  }

  if (quotedAnswers.length === 2) {
    return `${quotedAnswers[0]} or ${quotedAnswers[1]}`
  }

  return `${quotedAnswers.slice(0, -1).join(', ')} or ${quotedAnswers[quotedAnswers.length - 1]}`
}

// Builds the base descriptive name for a branch condition without any repeated-group suffix.
const buildBranchSectionBaseName = (
  parentSection: ISurveySection | undefined,
  branchingFilters?: {
    operation?: BranchingOperation
    prompt?: string
    itemId?: string
    answer?: string[] | null
  } | null
): string => {
  const questionIndex = parentSection?.surveyItems?.findIndex((item: Question) => item.itemId === branchingFilters?.itemId)
  const questionNumber = questionIndex !== undefined && questionIndex >= 0 ? questionIndex + 1 : 1
  const prompt = branchingFilters?.prompt ?? ''

  if (branchingFilters?.operation === BranchingOperation.Is) {
    return `Q${questionNumber}. "${prompt}" is ${formatBranchSectionAnswers(branchingFilters?.answer)}`
  }

  if (branchingFilters?.operation === BranchingOperation.IsNotEmpty) {
    return `Q${questionNumber}. "${prompt}" is answered`
  }

  return `Q${questionNumber}. "${prompt}" is unanswered`
}

// Builds the displayed name for a branched section and appends a numeric suffix for repeated groups.
const buildBranchSectionName = (
  sections: ISurveySection[],
  parentSection: ISurveySection | undefined,
  branchSection: ISurveySection
): string => {
  const baseName = buildBranchSectionBaseName(parentSection, branchSection.branchingFilters)
  const matchingGroupSections = sections.filter((section) =>
    section.parentSurveySectionId === branchSection.parentSurveySectionId &&
    getBranchGroupKey(section) === getBranchGroupKey(branchSection)
  )

  // Track the generated suffixes already consumed by this exact branch group so
  // newly created default names do not collide after earlier siblings are deleted.
  const usedSuffixes = matchingGroupSections.reduce<number[]>((suffixes, section) => {
    if (section.name === baseName) {
      suffixes.push(1)
      return suffixes
    }

    if (section.name.startsWith(`${baseName} (`) && section.name.endsWith(')')) {
      const suffixValue = Number(section.name.slice(baseName.length + 2, -1))
      if (!Number.isNaN(suffixValue) && suffixValue > 1) {
        suffixes.push(suffixValue)
      }
    }

    return suffixes
  }, [])

  let nextSuffix = usedSuffixes.length > 0 ? Math.max(...usedSuffixes) + 1 : 1
  let candidateName = nextSuffix === 1 ? baseName : `${baseName} (${nextSuffix})`

  while (sections.some((section) => section.name === candidateName)) {
    nextSuffix += 1
    candidateName = `${baseName} (${nextSuffix})`
  }

  return candidateName
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const getGeneratedBranchSectionNameSuffix = (name: string, baseName: string): number | null => {
  if (name === baseName) {
    return 1
  }

  const generatedSuffixMatch = new RegExp(`^${escapeRegExp(baseName)} \\((\\d+)\\)$`).exec(name)
  if (!generatedSuffixMatch) {
    return null
  }

  const suffixValue = Number(generatedSuffixMatch[1])
  return Number.isInteger(suffixValue) && suffixValue >= 2 ? suffixValue : null
}

const isGeneratedBranchSectionName = (
  section: ISurveySection,
  parentSection: ISurveySection | undefined
): boolean => {
  const baseName = buildBranchSectionBaseName(parentSection, section.branchingFilters)
  return getGeneratedBranchSectionNameSuffix(section.name, baseName) !== null
}

const buildNextAvailableBranchGroupName = (
  reservedNames: Set<string>,
  baseName: string,
  preferredSuffix: number
): string => {
  let nextSuffix = preferredSuffix
  let candidateName = nextSuffix === 1 ? baseName : `${baseName} (${nextSuffix})`

  while (reservedNames.has(candidateName)) {
    nextSuffix += 1
    candidateName = `${baseName} (${nextSuffix})`
  }

  reservedNames.add(candidateName)
  return candidateName
}

const syncBranchGroupNamesInParentSection = (
  sections: ISurveySection[],
  parentSection: ISurveySection | undefined,
  targetSection?: ISurveySection
): void => {
  if (!parentSection?.surveyItems || !targetSection?.parentSurveySectionId || !targetSection.branchingFilters) {
    return
  }

  sections.forEach((section) => {
    if (
      section.parentSurveySectionId === targetSection.parentSurveySectionId &&
      getBranchGroupKey(section) === getBranchGroupKey(targetSection)
    ) {
      updateBranchingFiltersSectionNameInParentSection(parentSection, section.surveySectionId, section.name)
    }
  })
}

const normalizeGeneratedBranchGroupNames = (
  sections: ISurveySection[],
  parentSection: ISurveySection | undefined,
  targetSection?: ISurveySection
): void => {
  if (!targetSection?.parentSurveySectionId || !targetSection.branchingFilters) {
    return
  }

  const branchGroupSections = sections.filter((section) =>
    section.parentSurveySectionId === targetSection.parentSurveySectionId &&
    getBranchGroupKey(section) === getBranchGroupKey(targetSection)
  )

  const generatedSectionIds = new Set(
    branchGroupSections
      .filter((section) => isGeneratedBranchSectionName(section, parentSection))
      .map((section) => section.surveySectionId)
  )
  const reservedNames = new Set(
    sections
      .filter((section) => !generatedSectionIds.has(section.surveySectionId))
      .map((section) => section.name)
  )

  let generatedNameIndex = 1
  branchGroupSections.forEach((section) => {
    // Only renumber names that still follow the generated branch-name pattern.
    // User-customized section names should be left untouched.
    if (!isGeneratedBranchSectionName(section, parentSection)) {
      return
    }

    const baseName = buildBranchSectionBaseName(parentSection, section.branchingFilters)
    section.name = buildNextAvailableBranchGroupName(reservedNames, baseName, generatedNameIndex)
    generatedNameIndex += 1
  })
}

// Inserts a branched section at the grouped position computed for its branch condition.
const insertBranchSection = (sections: ISurveySection[], branchSection: ISurveySection): ISurveySection[] => {
  const insertIndex = findBranchInsertIndex(sections, branchSection)
  return [
    ...sections.slice(0, insertIndex),
    branchSection,
    ...sections.slice(insertIndex)
  ]
}

// Repositions a branch subtree so it moves with the correct branch group after filter edits.
const moveSectionSubtreeToGroupedPosition = (sections: ISurveySection[], surveySectionId: string): ISurveySection[] => {
  const rootIndex = sections.findIndex((section) => section.surveySectionId === surveySectionId)
  if (rootIndex === -1) {
    return sections
  }

  const subtreeIndices = getSectionSubtreeIndices(sections, rootIndex)
  const subtreeIndexSet = new Set(subtreeIndices)
  const subtreeSections = subtreeIndices.map((index) => sections[index])
  const [rootSection] = subtreeSections

  if (!rootSection?.parentSurveySectionId) {
    return sections
  }

  const remainingSections = sections.filter((_, index) => !subtreeIndexSet.has(index))
  const insertIndex = findBranchInsertIndex(remainingSections, rootSection)

  return [
    ...remainingSections.slice(0, insertIndex),
    ...subtreeSections,
    ...remainingSections.slice(insertIndex)
  ]
}

// Regroup branch subtrees only when the effective branch condition actually changes.
const hasBranchConditionChanged = (
  section: ISurveySection,
  nextFilters?: { operation?: string, answer?: string[] } | null
): boolean => {
  const currentOperation = section.branchingFilters?.operation ?? null
  const currentAnswers: string[] = section.branchingFilters?.answer ?? []
  const nextOperation = nextFilters?.operation ?? null
  const nextAnswers: string[] = nextFilters?.answer ?? []

  if (currentOperation !== nextOperation) {
    return true
  }

  if (currentAnswers.length !== nextAnswers.length) {
    return true
  }

  return currentAnswers.some((answer: string, index: number) => answer !== nextAnswers[index])
}

const surveyTemplateSlice = createSlice({
  name: 'surveyTemplate',
  initialState,
  reducers: {
    resetState (state) {
      const newInitialSection = {
        ...initialSection,
        surveySectionId: uuidv4()
      }
      Object.assign(state, {
        ...initialState,
        surveySections: [newInitialSection]
      })
    },
    setTitle (state, action) {
      state.title = action.payload
    },
    addSection (state) {
      state.surveySections?.push({
        surveySectionId: uuidv4(),
        name: `Untitled section ${state.surveySections.length + 1}`,
        settings: [
        ],
        indentation: 0,
        parentSurveySectionId: null,
        branchingFilters: null
      })
    },
    removeSection (state, action: PayloadAction<{ section: ISurveySection }>) {
      const { section } = action.payload
      // Remove the current section from the main surveySections array
      state.surveySections = state.surveySections.filter((s: ISurveySection) => s.surveySectionId !== section.surveySectionId)
      state.selectedSection = null

      // If a parentSurveySectionId exists, we need to clean up the branchingFilters in that section linked surveyItem as well
      if (section.parentSurveySectionId) {
        const surveySections = state.surveySections ?? []
        const parentSurveySection = surveySections.find((s: ISurveySection) => s.surveySectionId === section.parentSurveySectionId)
        if (parentSurveySection?.surveyItems) {
          parentSurveySection.surveyItems?.forEach((item) => {
            if (item.branchingFilters) {
              item.branchingFilters = item.branchingFilters.filter(
                (filter) => filter.surveySectionId !== section.surveySectionId
              )
            }
          })
        }

        // Deleting the first generated branch in a group can leave the remaining
        // sibling at "(2)". Renormalize the generated names so the next add uses
        // the expected sequential suffix and does not create duplicates.
        normalizeGeneratedBranchGroupNames(state.surveySections, parentSurveySection, section)
        syncBranchGroupNamesInParentSection(state.surveySections, parentSurveySection, section)
      }
    },
    setDraftSurvey (state, action: PayloadAction<{ title: string, surveySections: ISurveySection[] }>) {
      state.title = action.payload?.title ?? ''
      state.surveySections = action.payload?.surveySections ?? [initialSection]
    },
    setSectionName (state, action) {
      const { surveySectionId, value } = action.payload
      const findSectionById = (sections: any[], id: any): any => sections.find((section) => section.surveySectionId === id)

      const sectionToUpdate = findSectionById(state.surveySections, surveySectionId)
      if (!sectionToUpdate) return

      sectionToUpdate.name = value
      // TODO: Review if we need to update the title as well
      // If Display title is Empty then Update Display title for The First time
      // if (!sectionToUpdate.title) {
      //   sectionToUpdate.title = value
      // }

      // Find the parent section
      const parentSurveySection = findSectionById(state.surveySections, sectionToUpdate.parentSurveySectionId)
      if (!parentSurveySection) return

      // Update branching filters in the parent section
      updateBranchingFiltersSectionNameInParentSection(parentSurveySection, surveySectionId, value)
    },

    setSectionTitle (state, action) {
      const { surveySectionId, value } = action.payload
      const sectionToUpdate = state.surveySections.find((section) => section.surveySectionId === surveySectionId)
      const valueIsEmpty = value === '' || value === null || value === undefined
      if (sectionToUpdate && valueIsEmpty) {
        delete sectionToUpdate.title
      } else if (sectionToUpdate) {
        sectionToUpdate.title = value
      }
    },

    reorderSections (state, action: PayloadAction<ISurveySection[]>) {
      state.surveySections = action.payload
    },
    setSectionDescription (state, action) {
      const { surveySectionId, value } = action.payload
      const sectionToUpdate = state.surveySections.find((section) => section.surveySectionId === surveySectionId)
      const valueIsEmpty = value === '' || value === null || value === undefined
      if (sectionToUpdate && valueIsEmpty) {
        // The API keeps the previous value for an omitted `descr`, so a cleared field sends null.
        sectionToUpdate.descr = null
      } else if (sectionToUpdate) {
        sectionToUpdate.descr = value
      }
    },

    moveQuestionUp (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex > 0 && questionIndex < section.surveyItems.length) {
        const surveyItems = section.surveyItems
        // Swap the question with the previous one
        const temp = surveyItems[questionIndex]
        surveyItems[questionIndex] = surveyItems[questionIndex - 1]
        surveyItems[questionIndex - 1] = temp
      }
    },
    moveQuestionDown (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex < section.surveyItems.length - 1) {
        const surveyItems = section.surveyItems
        const temp = surveyItems[questionIndex]
        surveyItems[questionIndex] = surveyItems[questionIndex + 1]
        surveyItems[questionIndex + 1] = temp
      }
    },

    deleteQuestionFromSection (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []

      // Find the section by surveySectionId
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)

      if (section?.surveyItems && questionIndex < section.surveyItems.length) {
        // Remove the question from the section's surveyItems array
        section.surveyItems.splice(questionIndex, 1)
      }
    },
    addSurveyQuestionInSection (state, action: PayloadAction<{ surveySectionId: string | undefined, question?: Question, removedQuestions?: Question[] }>) {
      const { surveySectionId, question, removedQuestions } = action.payload
      const surveySections = state.surveySections ?? []
      // Find the section by surveySectionId
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section) {
        let updatedSurveyItems = section?.surveyItems ?? []
        if (removedQuestions && removedQuestions.length > 0) {
          const removedIds = removedQuestions.map(q => q?.itemId)
          updatedSurveyItems = updatedSurveyItems.filter(q => !removedIds.includes(q.itemId))
        }
        if (question) {
          // Remove the question if it already exists (avoid duplicates)
          updatedSurveyItems = updatedSurveyItems.filter(oldQuestion => oldQuestion.itemId !== question.itemId)
          // Add the new question
          section.surveyItems = [...updatedSurveyItems, question]
        } else {
          // Only removals, no new question to add
          section.surveyItems = updatedSurveyItems
        }
      }
    },

    addFollowUpFreeResponseQuestionInSection (state, action: PayloadAction<{ surveySectionId: string | undefined, followUpParentQuestionIndex: number, questionTitle: string | undefined }>) {
      const { surveySectionId, followUpParentQuestionIndex, questionTitle } = action.payload

      const freeResponseFollowUpQuestion = {
        itemId: uuidv4(),
        isRequired: false,
        itemType: SurveyItemQuestionType.FREE_RESPONSE,
        prompt: `${PREFIX_FOLLOW_UP_FREE_RESPONSE_QUESTION_PROMPT} ${questionTitle}`,
        branchingFilters: null
      }
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section) {
        const updatedSurveyItems = section.surveyItems ?? []
        updatedSurveyItems.splice(followUpParentQuestionIndex + 1, 0, freeResponseFollowUpQuestion)
        section.surveyItems = updatedSurveyItems
      }
    },

    setSelectedQuestion (state, action) {
      state.selectedQuestion = action.payload
    },
    setSelectedSection (state, action) {
      state.selectedSection = action.payload
    },

    setfilters (state, action) {
      const { surveySectionId, branchingFilters, settings } = action.payload
      const { filters, parentSection, parentQuestion } = branchingFilters ?? {}
      const { operation, answer, prompt } = filters ?? {}

      const section = state.surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (!section) return
      if (settings) {
        section.settings = settings

        if (!section.parentSurveySectionId) {
          syncDirectBranchedChildrenWithParentSettings(state.surveySections, surveySectionId, settings)
        }
      }

      // Handle Branching Filters
      if (section.parentSurveySectionId && filters && parentSection?.surveyItems && parentQuestion) {
        const shouldRegroupSection = hasBranchConditionChanged(section, filters)
        const parentSurveyItems = state.surveySections.find((s: ISurveySection) => s?.surveySectionId === parentSection?.surveySectionId)?.surveyItems ?? []

        if (!parentSurveyItems.length) return

        const questionIndex = parentSection.surveyItems?.findIndex((item: Question) => item.itemId === parentQuestion.itemId)

        if (questionIndex === -1) return

        const newBranchFilter = preparedBranchFilterForUpdateFilters(operation, answer, section)

        const currentBranchingFilters = parentSurveyItems[questionIndex].branchingFilters

        const currentBranching = currentBranchingFilters?.filter(d => d.surveySectionId !== surveySectionId)
        // Update parent question with the branching filter
        parentSurveyItems[questionIndex] = {
          ...parentSurveyItems[questionIndex],
          branchingFilters: [...(currentBranching ?? []), ...(newBranchFilter ?? [])]

        }

        // Update current section's branching filters
        section.branchingFilters = { ...section.branchingFilters, operation, answer }
        if (shouldRegroupSection) {
          state.surveySections = moveSectionSubtreeToGroupedPosition(state.surveySections, surveySectionId)
        }
      }
    },

    // If the Parent Question prompt changes, we also need to update the Branch filter prompt
    updateBranchFilterPrompt (state, action) {
      const { prompt, childBranches } = action.payload

      state.surveySections.forEach((section) => {
        const isMatch = childBranches?.some(
          (_branch: IBranchingSurveyItems) => _branch.surveySectionId === section.surveySectionId
        )

        if (isMatch) {
          section.branchingFilters = {
            ...section.branchingFilters,
            prompt
          }
        }
      })
    },

    // If the Parent Question Option changes, we also need to update the Branch filter Answers
    updateBranchFilterAnswers (state, action) {
      const { oldKey, newKey, parentQuestionId, parentSurveySectionId } = action.payload
      state.surveySections.forEach((section) => {
        const isMatchingParentQuestion = section.parentSurveySectionId === parentSurveySectionId &&
          section.branchingFilters?.itemId === parentQuestionId
        const containsRenamedAnswer = section.branchingFilters?.answer?.includes(oldKey)

        if (isMatchingParentQuestion && containsRenamedAnswer) {
          const updatedAnswers = section.branchingFilters?.answer?.map((answer: string) =>
            answer === oldKey ? newKey : answer
          )
          section.branchingFilters = {
            ...section.branchingFilters,
            answer: updatedAnswers
          }
        }
      })
    },

    setEditableQuestion (state, action: PayloadAction<IEditableQuestion | null>) {
      if (action.payload?.question) {
        state.editableQuestion = {
          sectionIndex: action.payload?.sectionIndex,
          questionIndex: action.payload?.questionIndex,
          question: action.payload.question
        }
        return
      }
      state.editableQuestion = null
    },

    updateQuestionInSection (state, action: PayloadAction<{ surveySectionId?: string, questionIndex: number, updatedQuestion: Question }>) {
      const { surveySectionId, questionIndex, updatedQuestion } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex < section.surveyItems.length) {
        section.surveyItems[questionIndex] = updatedQuestion
      }
      state.editableQuestion = null
    },

    setEditableSurvey (state, action) {
      state.editableSurvey = action.payload
      state.title = action.payload?.title
      state.surveySections = action.payload?.surveySections
    },
    setBranchModal (state, action) {
      state.branchModal = action.payload
    },
    addBranchSection (state, action) {
      const { parentSurveySectionId, branchingFilters } = action.payload
      const { operation, answer, prompt, itemType, itemId } = branchingFilters ?? {}
      const parentSection = state.surveySections.find((s: ISurveySection) => s.surveySectionId === parentSurveySectionId)
      const initialBranchSettings = getBranchedSectionInitialSettings(parentSection)

      const newBranchSection = {
        surveySectionId: uuidv4(),
        name: '',
        isRepeatPerInstructor: initialBranchSettings[0]?.isRepeatPerInstructor ?? false,
        isSelectInstructorEnabled: false,
        parentSurveySectionId,
        indentation: parentSection ? parentSection.indentation + 1 : 1,
        settings: initialBranchSettings,
        branchingFilters: {
          operation,
          prompt,
          itemType,
          itemId,
          answer
        }
      }
      newBranchSection.name = buildBranchSectionName(state.surveySections, parentSection, newBranchSection)

      state.surveySections = insertBranchSection(state.surveySections, newBranchSection)
      // Normalize generated sibling names after insertion so the visible default
      // names for this branch group stay contiguous.
      normalizeGeneratedBranchGroupNames(state.surveySections, parentSection, newBranchSection)

      // Logic for adding BranchFilters in parent Question of this Branch Section
      const surveyItems = state.surveySections.find(s => s.surveySectionId === parentSurveySectionId)?.surveyItems ?? []
      const branchFilter = preparedBranchFilterForUpdateFilters(operation, answer, newBranchSection)

      // Set branchingFilters in the corresponding parent survey item
      const parentSurveyItem = surveyItems.find(item => item.itemId === itemId)
      if (surveyItems?.length && parentSurveyItem) {
        parentSurveyItem.branchingFilters = [...(parentSurveyItem?.branchingFilters ?? []), ...branchFilter]
      }
      syncBranchGroupNamesInParentSection(state.surveySections, parentSection, newBranchSection)

      notification('New Branch Created', { variant: 'success', subTitle: newBranchSection.name })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSurveyByIdApi.pending, (state) => {
        state.loading = true
      })
      .addCase(getSurveyByIdApi.fulfilled, (state, action) => {
        state.loading = false
      })
      .addCase(getSurveyByIdApi.rejected, (state, action) => {
        state.loading = false
      })
      // create survey
      .addCase(createSurveyTemplate.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(createSurveyTemplate.fulfilled, (state, action) => {
        state.actionLoading = false
        notification('Survey added successfully', { variant: 'success', subTitle: action.payload?.title })
      })
      .addCase(createSurveyTemplate.rejected, (state, action) => {
        state.actionLoading = false
        // Parse and show API validation errors (e.g. duplicate questions in a section)
        const errorMessage = extractSurveyErrorMessage(action.payload)
        notification(errorMessage, { variant: 'error' })
      })

      // update survey
      .addCase(editSurveyTemplate.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(editSurveyTemplate.fulfilled, (state, action) => {
        state.actionLoading = false
        const subTitle = buildSurveyTemplateChangeSummary(action.payload?.changes)
        notification('Survey Template saved', { variant: 'success', subTitle })
      })
      .addCase(editSurveyTemplate.rejected, (state, action) => {
        state.actionLoading = false
        // Parse and show API validation errors (e.g. duplicate questions in a section)
        const errorMessage = extractSurveyErrorMessage(action.payload)
        notification(errorMessage, { variant: 'error' })
      })
  }
})

export const {
  resetState,
  setTitle,
  addSection,
  removeSection,
  setSectionName,
  setSectionTitle,
  setDraftSurvey,
  setSectionDescription,
  addSurveyQuestionInSection,
  addFollowUpFreeResponseQuestionInSection,
  setSelectedQuestion,
  setSelectedSection,
  moveQuestionUp,
  moveQuestionDown,
  deleteQuestionFromSection,
  setEditableQuestion,
  updateQuestionInSection,
  setEditableSurvey,
  setfilters,
  addBranchSection,
  setBranchModal,
  updateBranchFilterPrompt,
  updateBranchFilterAnswers,
  reorderSections
} = surveyTemplateSlice.actions

export default surveyTemplateSlice.reducer
